/**
 * 控制台双语文案。
 *
 * **审查时补上的：在此之前控制台是纯中文、且完全没有 i18n 机制。**
 * 营销站是双语的，控制台不是 —— 一个英文用户能读懂定价页、付了钱、
 * 登录进来却看到一屏中文。而这个产品的 SEO 目标词恰恰是
 * “us stock market data api”，英文用户是主要客群，不是边缘情况。
 *
 * ## 与营销站的差异
 *
 * 营销站要被搜索引擎索引，所以两个 locale 各有独立路由（`/zh/`、`/en/`），
 * 由 Cloudflare Pages Function 在边缘协商，且**只对裸根路径生效**。
 *
 * 控制台在登录之后，不需要被索引，也就不需要路由前缀 —— 语言只是一个
 * 用户偏好。判定顺序仍与营销站一致：
 *
 *   1. 用户显式选过的语言（localStorage）—— 回访用户的选择最优先；
 *   2. `navigator.languages` 协商 —— zh* 走中文，其余走英文；
 *   3. 兜底 `en`。
 *
 * ## 为什么不用 i18next
 *
 * 控制台只有三个页面、约 90 条文案，一个带类型的普通对象足够，而且
 * **key 缺失是编译期错误**而不是运行期回退到 key 名。营销站同理。
 * 两边的 key 对等由 `npm run check:i18n` 强制（见 scripts/check-i18n.mjs）。
 */

export const LOCALES = ['zh', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_NAMES: Record<Locale, string> = { zh: '中文', en: 'English' };

const STORAGE_KEY = 'squawk.locale';

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** 读取用户显式选过的语言。读不到（隐私模式禁用 storage）不算错误。 */
export function storedLocale(): Locale | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* 存不下就只影响下次访问的默认值，不该让界面崩掉 */
  }
}

/**
 * 协商默认语言。
 *
 * 用 `navigator.languages` 而不是只看 `navigator.language`：前者是完整的
 * 偏好列表，后者只有第一项。一个把中文排在英文之后的用户应当拿到英文。
 */
export function negotiateLocale(
  languages: readonly string[] = typeof navigator === 'undefined' ? [] : navigator.languages ?? [],
): Locale {
  for (const tag of languages) {
    const lower = tag.toLowerCase();
    if (lower === 'zh' || lower.startsWith('zh-')) return 'zh';
    if (lower === 'en' || lower.startsWith('en-')) return 'en';
  }
  return 'en';
}

export function detectLocale(): Locale {
  return storedLocale() ?? negotiateLocale();
}

const zh = {
  common: {
    loading: '加载中…',
    loadingChart: '加载图表…',
    retry: '重试',
    cancel: '取消',
    dash: '—',
    language: '语言',
  },
  auth: {
    consoleName: 'Squawk 控制台',
    signInBlurb: '使用 LookAtWallStreet 账号登录，管理你的 API 密钥与用量。',
    signIn: '登录',
    signOut: '退出登录',
    retryLogin: '重新登录',
    completing: '正在完成登录…',
  },
  nav: {
    overview: '总览',
    keys: 'API 密钥',
    usage: '用量',
  },
  overview: {
    title: '总览',
    sub: '当前订阅与配额。',
    noAccessTitle: '当前无法访问数据 API。',
    noAccessFallback: '订阅未生效。',
    noAccessHint: '升级或续订后，已有密钥会自动恢复，无需重新创建。',
    graceTitle: '订阅已失效，正处于宽限期。',
    trialTitle: '试用期进行中。',
    plan: '套餐',
    apiAccess: 'API 访问',
    rpm: '每分钟请求上限',
    rpd: '每日请求上限',
    limitsTitle: '套餐限额',
    symbolsPerRequest: '单次请求最多标的数',
    lookback: '历史回溯上限',
    fullHistory: '全部历史',
    lookbackDays: (n: number) => `${n} 天`,
    maxKeys: 'API 密钥数上限',
    quickstart: '快速开始',
    quickstartNote: '价格为 ×10000 的整数；时间戳指向 K 线【开盘】时刻。详见 API 文档。',
    state: {
      allowed: '正常',
      trial: '试用期',
      grace: '宽限期内',
      denied_entitlement: '订阅未生效',
      denied_suspended: '账号已挂起',
    },
  },
  usage: {
    title: '用量',
    sub: '按美东交易日统计。配额计数最终一致，落库窗口 5 秒，因此当日数字可能略有滞后。',
    rangeDays: (n: number) => `${n} 天`,
    totalRequests: '请求总数',
    rowsReturned: '返回 K 线数',
    peakDay: '单日峰值',
    rateLimited: '被限流次数',
    limitedTitle: (n: number) => `出现过 ${n} 次限流。`,
    limitedHintBefore: '429 响应带 ',
    limitedHintAfter:
      '，请按它退避并加随机抖动。1 分钟粒度的数据每分钟轮询一次即可，更高频只会消耗配额。',
    chartTitle: '每日请求量',
    empty: '这段时间还没有调用记录。',
    seriesRequests: '请求数',
  },
  keys: {
    title: 'API 密钥',
    subBefore: '用于访问数据 API。请求头带 ',
    subAfter: ' 即可。',
    authHeaderSample: 'Authorization: Bearer <密钥>',
    create: '+ 创建密钥',
    count: (live: number, max: number) => `${live} / ${max} 把（已吊销的不计入）`,
    empty: '还没有密钥。点击「创建密钥」开始使用。',
    colKey: '密钥',
    colLabel: '备注',
    colStatus: '状态',
    colLastUsed: '最近使用',
    oldValidUntil: (when: string) => `旧密钥有效至 ${when}`,
    ipRestricted: (list: string) => `IP 限制：${list}`,
    rotate: '轮换',
    revoke: '吊销',
    suspendedTitle: '部分密钥已挂起。',
    suspendedHintBefore: '这是订阅失效导致的，',
    suspendedHintEmphasis: '不是吊销',
    suspendedHintAfter: ' —— 恢复订阅后密钥会自动重新启用，无需重建。',
    createTitle: '创建 API 密钥',
    createSub: '完整密钥只会在创建后显示一次。',
    labelField: '备注（便于日后辨认用途）',
    labelPlaceholder: '例如：生产环境回测服务',
    scopes: '作用域',
    ipField: 'IP 白名单（可选，逗号分隔的 CIDR）',
    creating: '创建中…',
    createAction: '创建',
    secretTitle: '请立即保存你的密钥',
    secretNoteCreate: '这是唯一一次显示完整密钥，请立即保存。',
    secretNoteRotate: (when: string) =>
      `新密钥已生效。旧密钥仍可用至 ${when}，之后自动失效。`,
    copy: '复制到剪贴板',
    saved: '我已保存',
    rotatePrompt:
      '旧密钥保留多久？输入 1h / 24h / 7d。\n\n' +
      '宽限期内新旧密钥同时有效，你可以先把新密钥部署上线，再淘汰旧的。',
    revokePrompt: (keyId: string) =>
      '吊销后无法恢复（与因欠费被挂起不同，后者续费即自动恢复）。\n\n' +
      `请输入密钥 ID 「${keyId}」以确认：`,
    status: {
      active: '启用中',
      suspended: '已挂起',
      revoked: '已吊销',
    },
  },
};
// ⚠️ 这里【不能】加 `as const`：那会把每条中文变成字面量类型，
//    于是 `const en: typeof zh` 要求英文逐字等于中文，编译必然失败。
//    我们要的是「结构相同」而不是「取值相同」。

const en: typeof zh = {
  common: {
    loading: 'Loading…',
    loadingChart: 'Loading chart…',
    retry: 'Retry',
    cancel: 'Cancel',
    dash: '—',
    language: 'Language',
  },
  auth: {
    consoleName: 'Squawk Console',
    signInBlurb: 'Sign in with your LookAtWallStreet account to manage API keys and usage.',
    signIn: 'Sign in',
    signOut: 'Sign out',
    retryLogin: 'Sign in again',
    completing: 'Completing sign-in…',
  },
  nav: {
    overview: 'Overview',
    keys: 'API keys',
    usage: 'Usage',
  },
  overview: {
    title: 'Overview',
    sub: 'Your current subscription and quotas.',
    noAccessTitle: 'Data API access is currently unavailable.',
    noAccessFallback: 'Subscription is not active.',
    noAccessHint:
      'Once you upgrade or renew, your existing keys resume automatically — no need to recreate them.',
    graceTitle: 'Subscription lapsed; you are in the grace period.',
    trialTitle: 'Trial in progress.',
    plan: 'Plan',
    apiAccess: 'API access',
    rpm: 'Requests per minute',
    rpd: 'Requests per day',
    limitsTitle: 'Plan limits',
    symbolsPerRequest: 'Symbols per request',
    lookback: 'Maximum lookback',
    fullHistory: 'Full history',
    lookbackDays: (n: number) => `${n} days`,
    maxKeys: 'API key limit',
    quickstart: 'Quick start',
    quickstartNote:
      'Prices are integers scaled by 10000; timestamps mark the bar OPEN. See the API docs for details.',
    state: {
      allowed: 'Active',
      trial: 'Trial',
      grace: 'Grace period',
      denied_entitlement: 'Subscription inactive',
      denied_suspended: 'Account suspended',
    },
  },
  usage: {
    title: 'Usage',
    sub: 'Counted by US/Eastern trading day. Quota counting is eventually consistent with a 5-second flush window, so today’s figures may lag slightly.',
    rangeDays: (n: number) => `${n}d`,
    totalRequests: 'Total requests',
    rowsReturned: 'Bars returned',
    peakDay: 'Peak day',
    rateLimited: 'Rate-limited',
    limitedTitle: (n: number) => `Rate-limited ${n} time(s).`,
    limitedHintBefore: '429 responses carry ',
    limitedHintAfter:
      '; back off accordingly and add random jitter. One poll per minute is enough for 1-minute bars — polling faster only burns quota.',
    chartTitle: 'Requests per day',
    empty: 'No calls recorded in this period.',
    seriesRequests: 'Requests',
  },
  keys: {
    title: 'API keys',
    subBefore: 'Used to access the data API. Send the header ',
    subAfter: ' with each request.',
    authHeaderSample: 'Authorization: Bearer <key>',
    create: '+ Create key',
    count: (live: number, max: number) => `${live} / ${max} used (revoked keys excluded)`,
    empty: 'No keys yet. Click “Create key” to get started.',
    colKey: 'Key',
    colLabel: 'Label',
    colStatus: 'Status',
    colLastUsed: 'Last used',
    oldValidUntil: (when: string) => `Previous key valid until ${when}`,
    ipRestricted: (list: string) => `IP allowlist: ${list}`,
    rotate: 'Rotate',
    revoke: 'Revoke',
    suspendedTitle: 'Some keys are suspended.',
    suspendedHintBefore: 'This is caused by an inactive subscription — ',
    suspendedHintEmphasis: 'not a revocation',
    suspendedHintAfter: '. Keys re-enable automatically once the subscription resumes.',
    createTitle: 'Create an API key',
    createSub: 'The full key is shown only once, right after creation.',
    labelField: 'Label (so you can tell keys apart later)',
    labelPlaceholder: 'e.g. production backtest service',
    scopes: 'Scopes',
    ipField: 'IP allowlist (optional, comma-separated CIDRs)',
    creating: 'Creating…',
    createAction: 'Create',
    secretTitle: 'Save your key now',
    secretNoteCreate: 'This is the only time the full key is shown. Save it now.',
    secretNoteRotate: (when: string) =>
      `The new key is live. The previous key keeps working until ${when}, then expires automatically.`,
    copy: 'Copy to clipboard',
    saved: 'I have saved it',
    rotatePrompt:
      'How long should the previous key stay valid? Enter 1h / 24h / 7d.\n\n' +
      'During the grace period both keys work, so you can roll out the new one before retiring the old.',
    revokePrompt: (keyId: string) =>
      'Revoking cannot be undone (unlike a billing suspension, which resumes on renewal).\n\n' +
      `Type the key ID “${keyId}” to confirm:`,
    status: {
      active: 'Active',
      suspended: 'Suspended',
      revoked: 'Revoked',
    },
  },
};

export const strings = { zh, en };
export type Strings = typeof zh;

export function t(locale: Locale): Strings {
  return strings[locale];
}
